// src/lib/nav-badges.js — Helpers PURS des badges de notification des menus
// PARENTS de la barre de navigation principale (ADR-269).
//
// Principe : un sous-onglet porteur d'un compteur « à traiter » (ex. pill
// « Message 2 » dans Communication) PROPAGE son compteur à son menu parent ;
// le parent affiche la SOMME des compteurs de ses sous-onglets. Zéro = pas de
// pastille. Les badges ENFANTS existants (pills MessagesPanel, segmented
// IncidentsTab) restent inchangés — la barre principale ne fait que refléter.
//
// Compteurs propagés (inventaire 2026-07-05) :
// - Communication ← Message (inbox_messages inbound non lus) + Demandes
//   (direct_booking_requests statut pending). Avis : pas de compteur (lecture
//   seule déonto, badge retiré volontairement — cf. MessagesPanel).
// - Opérations ← Incidents « à traiter » (en_cours + a_racheter). « Résolu »
//   est une ARCHIVE (compteur de liste, pas une notification) : exclu.
// - Autres catégories : aucun compteur enfant existant (Gestion,
//   Logements, Mes tâches, Paramètres). Prospection : « relances dues » est un
//   bouton-filtre interne alimenté par un fetch propre à l'onglet — non
//   propagé (coût d'un chargement prospects au niveau App non justifié).

import { STATUS } from "./incidents-api"

// Cap d'affichage : au-delà, la pastille affiche « 99+ » (le nombre exact
// n'apporte plus rien, et la pastille reste compacte dans la barre).
export const NAV_BADGE_CAP = 99

/**
 * Somme robuste d'un objet de compteurs { clef: n }. Ignore les valeurs
 * absentes, non numériques ou négatives (fail-safe : jamais NaN).
 * @param {Object<string, number>|null|undefined} counts
 * @returns {number}
 */
export function sumBadges(counts) {
  if (!counts || typeof counts !== "object") return 0
  return Object.values(counts).reduce((s, n) => {
    const v = Number(n)
    return s + (Number.isFinite(v) && v > 0 ? Math.floor(v) : 0)
  }, 0)
}

/**
 * Texte de la pastille. null = pas de pastille (zéro, invalide, négatif).
 * @param {number} n
 * @returns {string|null}
 */
export function formatBadgeCount(n) {
  const v = Number(n)
  if (!Number.isFinite(v) || v <= 0) return null
  return v > NAV_BADGE_CAP ? `${NAV_BADGE_CAP}+` : String(Math.floor(v))
}

/**
 * aria-label du bouton de menu parent quand une pastille est affichée —
 * lecteurs d'écran : « Communication — 3 éléments à traiter ».
 * @param {string} label libellé du menu (sans emoji)
 * @param {number} n compteur (exact, non cappé)
 * @returns {string}
 */
export function badgeAriaLabel(label, n) {
  const v = Math.floor(Number(n) || 0)
  return `${label} — ${v} élément${v > 1 ? "s" : ""} à traiter`
}

/**
 * Carte catégorie → compteur parent (SOMME des compteurs enfants). PURE :
 * App.jsx fournit les compteurs déjà chargés (hook notif + state incidents).
 * Chaque catégorie est INDÉPENDANTE : un compteur n'alimente qu'un parent.
 * @param {{unreadInbox?: number, pendingRequests?: number, incidentCounts?: Object|null}} p
 * @returns {{communication: number, operations: number}}
 */
export function computeNavBadges({ unreadInbox = 0, pendingRequests = 0, incidentCounts = null, aConfirmer = 0, fermeturesNonConfirmees = 0 } = {}) {
  // LOT 2 (2026-08-26) — le menu passe à CINQ destinations : les compteurs
  // suivent leurs écrans. « Voyageurs » ← messages non répondus + demandes en
  // attente (ex-Communication). « Aujourd'hui » ← incidents à traiter +
  // horaires à confirmer (ex-Opérations : ces sous-onglets vivent là
  // désormais). Les anciennes clés restent émises le temps que tout consomme
  // les nouvelles — même somme, jamais un second calcul.
  const voyageurs = sumBadges({ message: unreadInbox, demandes: pendingRequests })
  return {
    voyageurs,
    communication: voyageurs,
    // Calendrier (maquette NEST v677) : les fermetures non confirmées, le même
    // nombre que sa tuile (porte `ecran_fermetures_lire`).
    calendrier: sumBadges({ fermeturesNonConfirmees }),
    operations: sumBadges({
      enCours: incidentCounts?.[STATUS.EN_COURS],
      aRacheter: incidentCounts?.[STATUS.A_RACHETER],
      // STATUS.RESOLU volontairement absent — archive, pas une notification.
      // Couche confirmation (2026-08-18) : propositions d'horaires en attente
      // de la confirmation admin — propagées au parent Opérations (le badge
      // dédié, avec son urgence terracotta, vit sur le sous-onglet Demandes).
      aConfirmer,
    }),
  }
}

/**
 * La couleur d'une pastille du menu : chez l'admin, une action attend =
 * aubergine partout (maquette NEST v677) ; les autres espaces gardent leurs tons.
 */
export function tonPastilleMenu({ isAdmin, tons, id }) {
  return isAdmin ? "#7A2E6E" : (tons && tons[id]) || null
}

/** LOT 2 — la même somme, émise sous la clé de sa nouvelle destination. */
export function withJourAlias(badges) {
  return badges && typeof badges === 'object'
    ? { ...badges, jour: badges.operations || 0 }
    : badges
}
